import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

// ── Interactive Star Rating Input ─────────────────────────────────────────────
const StarRatingInput = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-brand-muted uppercase tracking-wide">
        Star Rating
      </label>
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }, (_, i) => {
          const starValue = i + 1;
          const filled = starValue <= (hovered || value);
          return (
            <button
              key={starValue}
              type="button"
              onClick={() => onChange(starValue)}
              onMouseEnter={() => setHovered(starValue)}
              onMouseLeave={() => setHovered(0)}
              className="focus:outline-none"
            >
              <svg
                className={`w-6 h-6 transition-colors ${filled ? "text-amber-400" : "text-gray-200"}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </button>
          );
        })}
        <span className="ml-1.5 text-sm font-medium text-brand-muted">{value} / 5</span>
      </div>
    </div>
  );
};

// ── Field ─────────────────────────────────────────────────────────────────────
const Field = ({ label, required, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-medium text-brand-text">
      {label}{" "}
      {required && <span className="text-danger">*</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full border border-surface-border rounded-lg px-3 py-2.5 text-sm text-brand-text placeholder:text-brand-light focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors bg-white";

// ── Section Heading ───────────────────────────────────────────────────────────
const SectionHeading = ({ children }) => (
  <p className="text-xs font-bold tracking-widest text-brand-muted uppercase mb-3">
    {children}
  </p>
);

// ── Default form state ────────────────────────────────────────────────────────
const defaultForm = {
  full_name: "",
  email: "",
  job_title: "",
  company: "",
  rating: 3,
  content: "",
};

// ── AddTestimonialModal ───────────────────────────────────────────────────────
const AddTestimonialModal = ({ isOpen, onClose, onSubmit, loading = false, initial = null }) => {
  const [form, setForm]     = useState(initial ?? defaultForm);
  const [errors, setErrors] = useState({});

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initial) {
        setForm({
          full_name: initial.full_name || "",
          email:     initial.email     || "",
          job_title: initial.job_title || "",
          company:   initial.company   || "",
          rating:    initial.rating    ?? 3,
          content:   initial.content   || "",
        });
      } else {
        setForm(defaultForm);
      }
      setErrors({});
    }
  }, [isOpen, initial]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  if (!isOpen) return null;

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim())     e.full_name = "Full name is required.";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Enter a valid email address.";
    if (!form.job_title.trim())     e.job_title = "Job title is required.";
    if (!form.company.trim())       e.company   = "Company is required.";
    if (form.content.trim().length < 30)
      e.content = "Testimonial must be at least 30 characters.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit?.({ ...form });
  };

  const isEdit = !!initial;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg z-10 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-surface-border flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-brand-text">
                {isEdit ? "Edit Testimonial" : "Add New Testimonial"}
              </h2>
              <p className="text-sm text-brand-muted mt-0.5">
                {isEdit
                  ? "Update the customer testimonial"
                  : "Collect and publish a customer testimonial"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-brand-light hover:text-brand-text transition-colors mt-0.5 flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex flex-col gap-5 flex-1">
          {/* Customer Information */}
          <div>
            <SectionHeading>Customer Information</SectionHeading>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Full Name" required>
                <input
                  type="text"
                  placeholder="e.g. Jane Smith"
                  value={form.full_name}
                  onChange={(e) => set("full_name", e.target.value)}
                  className={`${inputCls} ${errors.full_name ? "border-danger focus:ring-danger/30" : ""}`}
                />
                {errors.full_name && (
                  <p className="text-xs text-danger">{errors.full_name}</p>
                )}
              </Field>

              <Field label="Email (optional)">
                <input
                  type="text"
                  placeholder="jane@company.com"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={`${inputCls} ${errors.email ? "border-danger focus:ring-danger/30" : ""}`}
                />
                {errors.email && (
                  <p className="text-xs text-danger">{errors.email}</p>
                )}
              </Field>

              <Field label="Job Title / Role" required>
                <input
                  type="text"
                  placeholder="e.g. Chief Privacy Officer"
                  value={form.job_title}
                  onChange={(e) => set("job_title", e.target.value)}
                  className={`${inputCls} ${errors.job_title ? "border-danger focus:ring-danger/30" : ""}`}
                />
                {errors.job_title && (
                  <p className="text-xs text-danger">{errors.job_title}</p>
                )}
              </Field>

              <Field label="Company" required>
                <input
                  type="text"
                  placeholder="e.g. Acme Corp"
                  value={form.company}
                  onChange={(e) => set("company", e.target.value)}
                  className={`${inputCls} ${errors.company ? "border-danger focus:ring-danger/30" : ""}`}
                />
                {errors.company && (
                  <p className="text-xs text-danger">{errors.company}</p>
                )}
              </Field>
            </div>

            {/* Star Rating */}
            <div className="mt-4">
              <StarRatingInput value={form.rating} onChange={(v) => set("rating", v)} />
            </div>
          </div>

          {/* Testimonial Content */}
          <div>
            <SectionHeading>Testimonial Content</SectionHeading>
            <Field label="Testimonial Text" required>
              <span className="text-xs text-brand-muted -mt-1">(min 30 characters)</span>
              <textarea
                rows={5}
                placeholder="Paste or type the customer's testimonial here..."
                value={form.content}
                onChange={(e) => set("content", e.target.value)}
                className={`${inputCls} resize-none ${errors.content ? "border-danger focus:ring-danger/30" : ""}`}
              />
              <div className="flex justify-between items-center">
                {errors.content ? (
                  <p className="text-xs text-danger">{errors.content}</p>
                ) : (
                  <span />
                )}
                <span className="text-xs text-brand-light ml-auto">
                  {form.content.length} chars
                </span>
              </div>
            </Field>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-border flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-surface-border text-sm font-semibold text-brand-text hover:bg-surface transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {isEdit ? "Save Changes" : "Add Testimonial"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTestimonialModal;
